import { useState } from "react";
import { toast } from "sonner";
import { Printer, FileDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { api, fileUrl } from "../lib/api";
import { saveBlob } from "../lib/download";

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const KEYS = ["present", "late", "absent", "leave", "permission"];

const buildHtml = ({ employee, month, data, t, company }) => {
  const sm = data.summary || {};
  const late = (data.records || []).reduce((a, r) => a + (r.late_minutes || 0), 0);
  const boxes = [...KEYS.map((k) => [t(`dash.att.st.${k}`), sm[k] || 0]), [t("dash.att.workHours"), sm.hours || 0], ["دقائق التأخير", late]];
  const info = [["الرقم الوظيفي", employee?.employee_number], ["المسمى", employee?.job_title || employee?.profession], ["الفرع", employee?.branch], ["الهوية/الإقامة", employee?.iqama_number]].filter(([, v]) => v);
  const rows = (data.records || []).map((r) => `<tr>
    <td><bdi>${esc(r.date)}</bdi></td><td>${esc(t(`dash.att.st.${r.status}`))}${r.late_minutes > 0 ? ` (+${r.late_minutes} د)` : ""}</td>
    <td><bdi>${esc(r.check_in || "—")}</bdi></td><td><bdi>${esc(r.check_out || "—")}</bdi></td><td>${esc(r.hours ?? "—")}</td><td>${esc(r.notes || "")}</td></tr>`).join("");
  return `<div style="direction:rtl;font-family:'IBM Plex Sans Arabic',Tahoma,sans-serif;color:#0f172a;font-size:12px">
  <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #1e40af;padding-bottom:10px;margin-bottom:12px">
    <div><div style="font-size:18px;font-weight:700;color:#1e40af">${esc(company?.name || "")}</div><div style="color:#64748b">سجل الحضور والانصراف — شهر <bdi>${esc(month)}</bdi></div></div>
    ${company?.logo_id ? `<img src="${fileUrl(company.logo_id)}" crossorigin="anonymous" style="height:56px;max-width:140px;object-fit:contain" />` : ""}
  </div>
  <div style="font-size:16px;font-weight:700;margin-bottom:6px">${esc(employee?.name || "")}</div>
  <div style="display:flex;flex-wrap:wrap;gap:4px 18px;margin-bottom:12px">${info.map(([l, v]) => `<span><span style="color:#64748b">${l}:</span> <b><bdi>${esc(v)}</bdi></b></span>`).join("")}</div>
  <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:6px;margin-bottom:12px">${boxes.map(([l, v]) => `<div style="border:1px solid #cbd5e1;border-radius:8px;padding:6px;text-align:center"><div style="color:#64748b;font-size:11px">${esc(l)}</div><div style="font-weight:700;font-size:15px">${esc(v)}</div></div>`).join("")}</div>
  <style>.attx th,.attx td{border:1px solid #cbd5e1;padding:5px 6px;text-align:right}</style>
  <table class="attx" style="width:100%;border-collapse:collapse">
    <thead style="background:#eff6ff"><tr><th>${t("dash.att.colDate")}</th><th>${t("dash.att.colStatus")}</th><th>${t("dash.att.colIn")}</th><th>${t("dash.att.colOut")}</th><th>${t("dash.att.colHours")}</th><th>ملاحظات</th></tr></thead>
    <tbody>${rows || `<tr><td colspan="6" style="text-align:center;color:#94a3b8">${t("dash.att.noMonthRecords")}</td></tr>`}</tbody>
  </table>
  <div style="display:flex;justify-content:space-between;margin-top:40px;gap:40px">
    <div style="flex:1;text-align:center">توقيع الموظف<div style="border-top:1px solid #334155;margin-top:36px"></div></div>
    <div style="flex:1;text-align:center">توقيع المدير<div style="border-top:1px solid #334155;margin-top:36px"></div></div>
  </div>
  <div style="color:#94a3b8;font-size:10px;margin-top:14px">تاريخ الإصدار: <bdi>${new Date().toLocaleString("en-GB")}</bdi></div>
</div>`;
};

const fname = (employee, month, ext) => `سجل_حضور_${(employee?.name || "").replace(/\s+/g, "_")}_${month}.${ext}`;

const doPrint = (html) => {
  const w = window.open("", "_blank");
  if (!w) { toast.error("اسمح بالنوافذ المنبثقة للطباعة"); return; }
  w.document.write(`<html dir="rtl"><head><title>سجل الحضور</title><style>@page{size:A4;margin:12mm} body{margin:0}</style></head><body>${html}</body></html>`);
  w.document.close();
  w.onload = () => { w.focus(); w.print(); };
};

const doPdf = async (html, name) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0;width:794px;padding:28px;background:#fff";
  host.innerHTML = html;
  document.body.appendChild(host);
  try {
    const cv = await html2canvas(host, { scale: 1.6, backgroundColor: "#ffffff", useCORS: true });
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const pageH = (cv.width * 297) / 210;
    for (let y = 0, p = 0; y < cv.height; y += pageH, p++) {
      const part = document.createElement("canvas");
      part.width = cv.width; part.height = Math.min(pageH, cv.height - y);
      part.getContext("2d").drawImage(cv, 0, y, cv.width, part.height, 0, 0, cv.width, part.height);
      if (p) pdf.addPage();
      pdf.addImage(part.toDataURL("image/jpeg", 0.85), "JPEG", 0, 0, 210, (part.height * 210) / cv.width);
    }
    await saveBlob(pdf.output("blob"), name, "application/pdf");
  } finally { host.remove(); }
};

const doExcel = async ({ employee, month, data, t }, name) => {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("سجل الحضور", { views: [{ rightToLeft: true }] });
  const sm = data.summary || {};
  ws.addRow([`سجل الحضور — ${employee?.name || ""} — ${month}`]).font = { bold: true, size: 14 };
  ws.addRow(["الرقم الوظيفي", employee?.employee_number || "", "الفرع", employee?.branch || ""]);
  ws.addRow([...KEYS.map((k) => `${t(`dash.att.st.${k}`)}: ${sm[k] || 0}`), `${t("dash.att.workHours")}: ${sm.hours || 0}`,
    `دقائق التأخير: ${(data.records || []).reduce((a, r) => a + (r.late_minutes || 0), 0)}`]);
  ws.addRow([]);
  const h = ws.addRow([t("dash.att.colDate"), t("dash.att.colStatus"), "دقائق التأخير", t("dash.att.colIn"), t("dash.att.colOut"), t("dash.att.colHours"), "ملاحظات"]);
  h.font = { bold: true }; h.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDBEAFE" } };
  (data.records || []).forEach((r) => ws.addRow([r.date, t(`dash.att.st.${r.status}`), r.late_minutes || 0, r.check_in || "", r.check_out || "", r.hours ?? "", r.notes || ""]));
  ws.columns.forEach((c, i) => { c.width = [14, 12, 12, 10, 10, 9, 30][i] || 14; });
  const buf = await wb.xlsx.writeBuffer();
  await saveBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), name);
};

export const AttendanceRecordExport = ({ employee, month, data, t }) => {
  const [busy, setBusy] = useState("");
  const run = (kind) => async () => {
    if (!data) return;
    setBusy(kind);
    try {
      const company = kind === "xlsx" ? null : await api.getCompany().catch(() => null);
      const ctx = { employee, month, data, t, company };
      if (kind === "print") doPrint(buildHtml(ctx));
      else if (kind === "pdf") await doPdf(buildHtml(ctx), fname(employee, month, "pdf"));
      else await doExcel(ctx, fname(employee, month, "xlsx"));
    } catch { toast.error("تعذّر التصدير"); } finally { setBusy(""); }
  };
  const B = ({ k, icon: Icon, label }) => (
    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" disabled={!data || !!busy} onClick={run(k)} data-testid={`att-record-${k}`}>
      {busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
    </Button>
  );
  return (
    <div className="flex flex-wrap gap-2" data-testid="att-record-export">
      <B k="print" icon={Printer} label="طباعة" />
      <B k="pdf" icon={FileDown} label="PDF" />
      <B k="xlsx" icon={FileSpreadsheet} label="Excel" />
    </div>
  );
};
